import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { CheckSquare, ChevronDown, ChevronUp } from 'lucide-react';
import html2canvas from 'html2canvas';
import { PDFDocument, PageSizes, degrees, rgb } from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';
import openSansRegular from 'open-sans-fonts/open-sans/Regular/OpenSans-Regular.ttf';
import openSansBold from 'open-sans-fonts/open-sans/Bold/OpenSans-Bold.ttf';
import authFetch from '../utils/authFetch';
import UniversalChecklistModal from './UniversalChecklistModal';
import {
  MASTER_PARCEL_TEMPLATE_PRESET,
  MPT_AREA_SECTION_ITEM_ID,
  MPT_ANALIZA_TRGA_ITEM_ID,
  MPT_GIS_IZPISI_ITEM_ID,
  MPT_IZRIS_E_PROSTOR_ITEM_ID,
  MPT_LOKACIJSKA_INFORMACIJA_ITEM_ID,
  MPT_OGLEDNICA_ITEM_ID,
  MPT_PREDSTAVITEV_PARCELE_ITEM_ID,
  MPT_ZK_ITEM_ID,
  createMasterParcelCustomFieldRenderer,
  isMasterParcelChecklistItemFilled,
  renderMasterParcelSummary
} from '../pages/sellingPropertyMpt';

const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocal ? 'http://localhost:5000' : 'https://bdinteldb.com';
const DOCUMENT_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt,.zip,.rar';
const MPT_TYPE_KEY = 'mpt';
const MPT_MULTI_DOCUMENT_ITEM_IDS = new Set([
  MPT_LOKACIJSKA_INFORMACIJA_ITEM_ID,
  MPT_IZRIS_E_PROSTOR_ITEM_ID,
  MPT_GIS_IZPISI_ITEM_ID,
  MPT_ANALIZA_TRGA_ITEM_ID,
  MPT_OGLEDNICA_ITEM_ID,
  MPT_PREDSTAVITEV_PARCELE_ITEM_ID
]);

const normalizeChecklistDocumentValue = (value) => {
  const documentName = String(value?.documentName || '').trim();
  const documentPath = String(value?.documentPath || '').trim();
  if (!documentName || !documentPath) {
    return null;
  }

  return { documentName, documentPath };
};

const getChecklistDocumentValues = (item, value) => {
  const documents = Array.isArray(value) ? value : [value];
  return documents.map((documentValue) => normalizeChecklistDocumentValue(documentValue)).filter(Boolean);
};

const isMultiDocumentChecklistItem = (item) => item?.type === 'document' && MPT_MULTI_DOCUMENT_ITEM_IDS.has(String(item.id || ''));

const parseNumberValue = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(String(value).replace(',', '.'));
  return Number.isNaN(parsed) ? null : parsed;
};

const computePricePerSquareMeter = ({ area, price }) => {
  const parsedArea = parseNumberValue(area);
  const parsedPrice = parseNumberValue(price);
  if (parsedArea === null || parsedPrice === null || parsedArea === 0) return null;
  return parsedPrice / parsedArea;
};

const formatEuro = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const num = Number(value);
  if (Number.isNaN(num)) return `${value} €`;
  return `${Math.round(num).toLocaleString('sl-SI')} €`;
};

const CHECKLIST_EXPORTABLE_MPT_TYPES = new Set([
  'mpt_zk_extracts',
  'mpt_access',
  'mpt_neighbor_parcels',
  'mpt_electric_network',
  'mpt_water_network',
  'mpt_gurs_izpisi',
  'mpt_vrednotenje_izpisi',
  'mpt_ostali_dokumenti'
]);

const isChecklistDocumentCapable = (item) => {
  if (!item) return false;
  if (item.type === 'document') return true;
  return CHECKLIST_EXPORTABLE_MPT_TYPES.has(String(item.type || ''));
};

const isChecklistValueFilled = (item, value) => {
  if (!item) return false;
  if (item.type === 'checkbox') return value === true;
  if (item.type === 'document') return getChecklistDocumentValues(item, value).length > 0;
  return String(value ?? '').trim() !== '';
};

const createZkPdfSource = ({ zkExtractId, ko, idParceleStavbe, label = '', history = false }) => {
  if (!zkExtractId) return null;

  const trimmedKo = String(ko || '').trim();
  const trimmedParcelId = String(idParceleStavbe || '').trim();
  const fallbackName = [trimmedKo, trimmedParcelId].filter(Boolean).join('_');

  return {
    kind: 'zk',
    zkExtractId,
    ko: trimmedKo,
    idParceleStavbe: trimmedParcelId,
    history,
    documentName: label || `${history ? 'ZK_History' : 'ZK_Extract'}_${fallbackName || zkExtractId}.pdf`
  };
};

const createDocumentPdfSource = (documentValue) => documentValue?.documentPath
  ? {
      kind: 'document',
      ...documentValue
    }
  : null;

const getChecklistItemDocumentValues = (item, value, propertyForm) => {
  if (!item) return [];

  if (item.type === 'document') {
    return getChecklistDocumentValues(item, value).map((documentValue) => createDocumentPdfSource(documentValue)).filter((documentValue) => documentValue?.documentPath);
  }

  if (item.type === 'mpt_zk_extracts') {
    const sources = [];
    const primarySource = createZkPdfSource({
      zkExtractId: propertyForm?.zkExtractId,
      ko: propertyForm?.katastrskaObcina,
      idParceleStavbe: propertyForm?.idParceleStavbe,
      label: `ZK extract - parcela 1${propertyForm?.katastrskaObcina || propertyForm?.idParceleStavbe ? ` (${[propertyForm?.katastrskaObcina, propertyForm?.idParceleStavbe].filter(Boolean).join(' ')})` : ''}`
    });
    const primaryHistorySource = createZkPdfSource({
      zkExtractId: propertyForm?.zkExtractId,
      ko: propertyForm?.katastrskaObcina,
      idParceleStavbe: propertyForm?.idParceleStavbe,
      label: `ZK history - parcela 1${propertyForm?.katastrskaObcina || propertyForm?.idParceleStavbe ? ` (${[propertyForm?.katastrskaObcina, propertyForm?.idParceleStavbe].filter(Boolean).join(' ')})` : ''}`,
      history: true
    });

    if (primarySource) sources.push(primarySource);
    if (primaryHistorySource) sources.push(primaryHistorySource);

    if (Array.isArray(value?.extraParcels)) {
      value.extraParcels.forEach((parcel, index) => {
        const source = createZkPdfSource({
          zkExtractId: parcel?.zkExtractId,
          ko: parcel?.ko,
          idParceleStavbe: parcel?.parcela,
          label: `ZK extract - parcela ${index + 2}${parcel?.ko || parcel?.parcela ? ` (${[parcel?.ko, parcel?.parcela].filter(Boolean).join(' ')})` : ''}`
        });

        const historySource = createZkPdfSource({
          zkExtractId: parcel?.zkExtractId,
          ko: parcel?.ko,
          idParceleStavbe: parcel?.parcela,
          label: `ZK history - parcela ${index + 2}${parcel?.ko || parcel?.parcela ? ` (${[parcel?.ko, parcel?.parcela].filter(Boolean).join(' ')})` : ''}`,
          history: true
        });

        if (source) sources.push(source);
        if (historySource) sources.push(historySource);
      });
    }

    return sources;
  }

  if (item.type === 'mpt_access') {
    if (!value) return [];
    const sources = [];

    const municipalSource = createZkPdfSource({
      zkExtractId: value?.municipalZkExtractId,
      ko: value?.ko,
      idParceleStavbe: value?.parcela,
      label: `ZK extract - dostop${value?.ko || value?.parcela ? ` (${[value?.ko, value?.parcela].filter(Boolean).join(' ')})` : ''}`
    });

    if (municipalSource) {
      sources.push(municipalSource);
    }

    if (Array.isArray(value?.parcels)) {
      value.parcels.forEach((parcel, index) => {
        const source = createZkPdfSource({
          zkExtractId: parcel?.zkExtractId,
          ko: parcel?.ko,
          idParceleStavbe: parcel?.parcela,
          label: `ZK extract - dostop parcela ${index + 1}${parcel?.ko || parcel?.parcela ? ` (${[parcel?.ko, parcel?.parcela].filter(Boolean).join(' ')})` : ''}`
        });

        if (source) {
          sources.push(source);
        }
      });
    }

    return sources;
  }

  if (item.type === 'mpt_neighbor_parcels') {
    if (!value) return [];
    return Array.isArray(value?.parcels)
      ? value.parcels
        .map((parcel, index) => createZkPdfSource({
          zkExtractId: parcel?.zkExtractId,
          ko: parcel?.ko,
          idParceleStavbe: parcel?.parcela,
          label: `ZK extract - sosednja parcela ${index + 1}${parcel?.ko || parcel?.parcela ? ` (${[parcel?.ko, parcel?.parcela].filter(Boolean).join(' ')})` : ''}`
        }))
        .filter(Boolean)
      : [];
  }

  if (item.type === 'mpt_electric_network' || item.type === 'mpt_water_network') {
    if (!value) return [];
    const locationDocuments = Array.isArray(value?.locationDocuments)
      ? value.locationDocuments
      : value?.locationDocument?.documentPath
        ? [value.locationDocument]
        : [];
    return locationDocuments.map((documentValue) => createDocumentPdfSource(documentValue)).filter((documentValue) => documentValue?.documentPath);
  }

  if (item.type === 'mpt_gurs_izpisi' || item.type === 'mpt_vrednotenje_izpisi') {
    if (!value) return [];
    return Array.isArray(value?.documents)
      ? value.documents.flatMap((entry) => {
          const documents = Array.isArray(entry?.documents)
            ? entry.documents
            : entry?.document?.documentPath
              ? [entry.document]
              : [];
          return documents.map((documentValue) => createDocumentPdfSource(documentValue)).filter((documentValue) => documentValue?.documentPath);
        })
      : [];
  }

  if (item.type === 'mpt_ostali_dokumenti') {
    if (!value) return [];
    return Array.isArray(value?.documents)
      ? value.documents.map((entry) => createDocumentPdfSource(entry?.document)).filter((documentValue) => documentValue?.documentPath)
      : [];
  }

  return [];
};

const isPdfDocumentValue = (documentValue) => {
  if (documentValue?.kind === 'zk') {
    return true;
  }

  return /\.pdf$/i.test(String(documentValue?.documentName || documentValue?.documentPath || '').trim());
};

const downloadArrayBufferAsFile = (arrayBuffer, filename) => {
  const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(anchor);
};

let cachedPdfFontPromise = null;

const loadPdfFonts = async (pdfDocument) => {
  if (!cachedPdfFontPromise) {
    cachedPdfFontPromise = Promise.all([
      fetch(openSansRegular).then((response) => response.arrayBuffer()),
      fetch(openSansBold).then((response) => response.arrayBuffer())
    ]);
  }

  const [regularFontBytes, boldFontBytes] = await cachedPdfFontPromise;
  pdfDocument.registerFontkit(fontkit);

  return {
    regularFont: await pdfDocument.embedFont(regularFontBytes),
    boldFont: await pdfDocument.embedFont(boldFontBytes)
  };
};

const waitForNextPaint = () => new Promise((resolve) => {
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(resolve);
  });
});

const createExportParcelList = (propertyForm, zkValue) => {
  const extraParcels = Array.isArray(zkValue?.extraParcels)
    ? zkValue.extraParcels.filter((parcel) => parcel && (parcel.ko || parcel.parcela || parcel.zkExtractId || parcel.ownerNames))
    : [];

  return [
    {
      label: 'parcela 1',
      ko: String(propertyForm?.katastrskaObcina || '').trim(),
      parcela: String(propertyForm?.idParceleStavbe || '').trim(),
      zkExtractId: propertyForm?.zkExtractId || null,
      ownerNames: ''
    },
    ...extraParcels.map((parcel, index) => ({
      label: `parcela ${index + 2}`,
      ko: String(parcel?.ko || '').trim(),
      parcela: String(parcel?.parcela || '').trim(),
      zkExtractId: Number.isInteger(Number(parcel?.zkExtractId)) ? Number(parcel.zkExtractId) : null,
      ownerNames: String(parcel?.ownerNames || '').trim()
    }))
  ].filter((parcel) => parcel.ko || parcel.parcela || parcel.zkExtractId || parcel.ownerNames);
};

const formatChecklistValue = (item, value) => {
  if (item?.type === 'checkbox') {
    return value === true ? 'checked' : '';
  }

  if (item?.type === 'document') {
    return getChecklistDocumentValues(item, value).map((documentValue) => documentValue.documentName).join(', ');
  }

  return String(value ?? '').trim();
};

const addChecklistSummaryFromDomToPdf = async (mergedPdf, element, options = {}) => {
  if (!element) {
    throw new Error('Could not render checklist export.');
  }

  await waitForNextPaint();

  const itemElements = Array.from(element.querySelectorAll('.checklist-export-item'));
  const bounds = element.getBoundingClientRect();
  if (bounds.width <= 0 || bounds.height <= 0 || itemElements.length === 0) {
    throw new Error('Checklist export surface was not rendered before capture.');
  }

  const [a4Width, a4Height] = PageSizes.A4;
  const overlayLabel = String(options.overlayLabel || '').trim();
  const overlayEnabled = options.overlayEnabled === true && overlayLabel;
  const overlayFont = options.overlayFont || null;
  const marginX = 24;
  const topMargin = 24;
  const bottomMargin = 24;
  const contentWidth = a4Width - marginX * 2;
  const itemGap = 10;
  const maxPageContentHeight = a4Height - topMargin - bottomMargin;
  let page = null;
  let cursorY = 0;

  const drawOverlay = (targetPage) => {
    if (overlayEnabled && overlayFont) {
      targetPage.drawText(overlayLabel, {
        x: 24,
        y: a4Height - 20,
        size: 10,
        font: overlayFont,
        color: rgb(0.2, 0.24, 0.31),
        opacity: 0.82
      });
    }
  };

  const startPage = () => {
    page = mergedPdf.addPage(PageSizes.A4);
    drawOverlay(page);
    cursorY = a4Height - topMargin;
  };

  startPage();

  for (const itemElement of itemElements) {
    const itemCanvas = await html2canvas(itemElement, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      logging: false,
      windowWidth: Math.max(document.documentElement.clientWidth, itemElement.scrollWidth || 0, 1200),
      windowHeight: Math.max(document.documentElement.clientHeight, itemElement.scrollHeight || 0, 1200)
    });

    if (!itemCanvas.width || !itemCanvas.height) {
      continue;
    }

    const fullRenderedHeight = (itemCanvas.height / itemCanvas.width) * contentWidth;
    const availableHeightBeforeItem = cursorY - bottomMargin;

    if (fullRenderedHeight <= maxPageContentHeight && fullRenderedHeight > availableHeightBeforeItem) {
      startPage();
    }

    let remainingTop = 0;
    while (remainingTop < itemCanvas.height) {
      const availableHeight = cursorY - bottomMargin;
      if (availableHeight <= 24) {
        startPage();
      }

      const recalculatedAvailableHeight = cursorY - bottomMargin;
      const maxSliceHeightPx = Math.max(1, Math.floor(itemCanvas.width * (recalculatedAvailableHeight / contentWidth)));
      const sliceHeightPx = Math.min(itemCanvas.height - remainingTop, maxSliceHeightPx);
      const sliceCanvas = document.createElement('canvas');
      sliceCanvas.width = itemCanvas.width;
      sliceCanvas.height = sliceHeightPx;

      const sliceContext = sliceCanvas.getContext('2d');
      if (!sliceContext) {
        throw new Error('Could not prepare checklist item canvas slice.');
      }

      sliceContext.fillStyle = '#ffffff';
      sliceContext.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
      sliceContext.drawImage(itemCanvas, 0, remainingTop, itemCanvas.width, sliceHeightPx, 0, 0, itemCanvas.width, sliceHeightPx);

      const embeddedImage = await mergedPdf.embedPng(sliceCanvas.toDataURL('image/png'));
      const renderedHeight = (sliceHeightPx / itemCanvas.width) * contentWidth;

      page.drawImage(embeddedImage, {
        x: marginX,
        y: cursorY - renderedHeight,
        width: contentWidth,
        height: renderedHeight
      });

      cursorY -= renderedHeight;
      remainingTop += sliceHeightPx;

      if (remainingTop < itemCanvas.height) {
        startPage();
      } else {
        cursorY -= itemGap;
      }
    }
  }
};

const addPdfCoverPage = async (mergedPdf, { propertyForm, parcelNumbers, parcelArea, pricePerSquareMeter, regularFont, boldFont }) => {
  const page = mergedPdf.addPage(PageSizes.A4);
  const [pageWidth, pageHeight] = PageSizes.A4;
  const centeredText = (text, y, options = {}) => {
    const font = options.font || regularFont;
    const size = options.size || 12;
    const renderedText = String(text || '');
    const width = font.widthOfTextAtSize(renderedText, size);
    page.drawText(renderedText, {
      x: (pageWidth - width) / 2,
      y,
      font,
      size,
      color: options.color || rgb(0.08, 0.1, 0.14)
    });
  };

  centeredText('Dokumentacija zemljišča', pageHeight - 110, { font: boldFont, size: 24 });

  const koNumber = String(propertyForm?.katastrskaObcina || '').trim() || 'Ni določeno';
  const koName = String(propertyForm?.koName || '').trim();
  const normalizedParcelNumbers = Array.isArray(parcelNumbers) ? parcelNumbers.filter(Boolean) : [];
  const parcelsText = normalizedParcelNumbers.length > 0 ? normalizedParcelNumbers.join(', ') : (String(propertyForm?.idParceleStavbe || '').trim() || 'Ni določeno');
  const areaText = parcelArea ? `${parcelArea} m²` : 'Ni določeno';
  const hasPrice = String(propertyForm?.price ?? '').trim() !== '';

  const detailLines = [
    `KO: ${koNumber}`,
    `${normalizedParcelNumbers.length <= 1 ? 'Parcela' : 'Parcele'}: ${parcelsText}`,
    `Velikost: ${areaText}`
  ];

  if (koName) {
    detailLines.splice(1, 0, `Ime KO: ${koName}`);
  }

  if (hasPrice) {
    detailLines.push(`Cena: ${formatEuro(propertyForm?.price)}`);
    if (pricePerSquareMeter !== null && pricePerSquareMeter !== undefined) {
      detailLines.push(`Cena / m²: ${formatEuro(pricePerSquareMeter)}`);
    }
  }

  detailLines.forEach((line, index) => {
    centeredText(line, pageHeight - 180 - index * 28, {
      size: 14,
      color: rgb(0.2, 0.24, 0.31)
    });
  });
};

const appendPdfToDocument = async (mergedPdf, pdfBytes, options = {}) => {
  const sourcePdf = await PDFDocument.load(pdfBytes);
  const overlayLabel = String(options.overlayLabel || '').trim();
  const overlayEnabled = options.overlayEnabled === true && overlayLabel;
  const overlayFont = options.overlayFont || null;

  const copiedPages = await mergedPdf.copyPages(sourcePdf, sourcePdf.getPageIndices());
  copiedPages.forEach((page) => {
    mergedPdf.addPage(page);

    if (overlayEnabled && overlayFont) {
      const { height } = page.getSize();
      page.drawText(overlayLabel, {
        x: 24,
        y: height - 20,
        size: 10,
        font: overlayFont,
        color: rgb(0.2, 0.24, 0.31),
        opacity: 0.82
      });
    }
  });
};

const MasterParcelChecklistSection = ({
  entityId,
  entityType,
  scope,
  propertyForm,
  propertyContacts = [],
  onOpenPropertyContact,
  title = 'MPT Checklist',
  defaultExpanded = true
}) => {
  const checklistExportRef = useRef(null);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [checklistValues, setChecklistValues] = useState({});
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [checklistSaving, setChecklistSaving] = useState(false);
  const [showChecklistExportModal, setShowChecklistExportModal] = useState(false);
  const [checklistExportSelection, setChecklistExportSelection] = useState([]);
  const [checklistExportIncludeCoverPage, setChecklistExportIncludeCoverPage] = useState(true);
  const [checklistExportIncludeItemName, setChecklistExportIncludeItemName] = useState(false);
  const [checklistExporting, setChecklistExporting] = useState(false);
  const [checklistExportSurfaceVisible, setChecklistExportSurfaceVisible] = useState(false);

  const ensureTemplate = useCallback(async () => {
    const response = await authFetch(`${API_BASE}/api/checklists/template`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        scope,
        typeKey: MPT_TYPE_KEY,
        items: MASTER_PARCEL_TEMPLATE_PRESET.items
      })
    });

    if (!response.ok) {
      throw new Error('Failed to prepare MPT template');
    }
  }, [scope]);

  const loadChecklistData = useCallback(async () => {
    if (!entityId || !entityType) {
      setChecklistValues({});
      return;
    }

    setChecklistLoading(true);
    try {
      await ensureTemplate();
      const params = new URLSearchParams({
        scope,
        typeKey: MPT_TYPE_KEY,
        entityType,
        entityId: String(entityId)
      });
      const response = await authFetch(`${API_BASE}/api/checklists/entry?${params.toString()}`);
      if (!response.ok) {
        throw new Error('Failed to fetch checklist');
      }

      const data = await response.json();
      setChecklistValues(data.values && typeof data.values === 'object' ? data.values : {});
    } catch (error) {
      console.error(error);
      setChecklistValues({});
    } finally {
      setChecklistLoading(false);
    }
  }, [ensureTemplate, entityId, entityType, scope]);

  useEffect(() => {
    loadChecklistData();
  }, [loadChecklistData]);

  const handleChecklistDocumentUpload = async (_item, file) => {
    if (!file) return null;

    const formData = new FormData();
    formData.append('document', file);

    const response = await authFetch(`${API_BASE}/api/checklists/documents`, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to upload document');
    }

    const data = await response.json();
    return {
      documentName: data.documentName,
      documentPath: data.documentPath
    };
  };

  const handleDocumentDownload = async (documentPath, documentName) => {
    if (!documentPath) return;

    const response = await authFetch(`${API_BASE}/api/leads/documents/${documentPath}`);
    if (!response.ok) {
      throw new Error('Failed to download document');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = documentName || documentPath;
    document.body.appendChild(anchor);
    anchor.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(anchor);
  };

  const fetchChecklistDocumentArrayBuffer = useCallback(async (documentValue) => {
    if (documentValue?.kind === 'zk') {
      const response = await authFetch(`${API_BASE}/api/zk/${documentValue.history ? 'download-history' : 'download'}/${documentValue.zkExtractId}`);
      if (!response.ok) {
        throw new Error(`Failed to download ${documentValue.documentName || 'ZK document'}`);
      }

      return response.arrayBuffer();
    }

    if (!documentValue?.documentPath) {
      throw new Error('Missing document path');
    }

    const response = await authFetch(`${API_BASE}/api/leads/documents/${documentValue.documentPath}`);
    if (!response.ok) {
      throw new Error(`Failed to download ${documentValue.documentName || 'document'}`);
    }

    return response.arrayBuffer();
  }, []);

  const handleDownloadZkFile = async ({ zkExtractId, ko, idParceleStavbe, history = false }) => {
    if (!zkExtractId) {
      window.alert(`No ZK ${history ? 'history' : 'extract'} available for this property.`);
      return;
    }

    try {
      const response = await authFetch(`${API_BASE}/api/zk/${history ? 'download-history' : 'download'}/${zkExtractId}`);
      if (!response.ok) {
        throw new Error(response.status === 404 ? `ZK ${history ? 'history' : 'extract'} file not found` : `Failed to download ZK ${history ? 'history' : 'extract'}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${history ? 'ZK_History' : 'ZK_Extract'}_${ko}_${idParceleStavbe}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(anchor);
    } catch (error) {
      console.error(error);
      window.alert(`Could not download ZK ${history ? 'history' : 'extract'}: ${error.message}`);
    }
  };

  const fetchZkParcelData = useCallback(async (ko, parcela) => {
    const response = await authFetch(`${API_BASE}/api/zk/latest?ko=${encodeURIComponent(ko)}&id=${encodeURIComponent(parcela)}`);

    if (!response.ok) {
      if (response.status === 404) {
        return { zkExtractId: null, ownerNames: '', owners: [] };
      }

      throw new Error('Failed to fetch ZK parcel data');
    }

    const data = await response.json();
    return {
      zkExtractId: data.zkId || null,
      ownerNames: String(data.ownerNames || '').trim(),
      owners: Array.isArray(data.owners) ? data.owners : []
    };
  }, []);

  const filledChecklistItems = useMemo(() => MASTER_PARCEL_TEMPLATE_PRESET.items
    .map((item) => ({ item, value: checklistValues[item.id] }))
    .filter(({ item, value }) => {
      if (item.type === 'document') {
        return isChecklistValueFilled(item, value);
      }

      return isMasterParcelChecklistItemFilled({
        item,
        value,
        price: propertyForm?.price,
        allValues: checklistValues
      });
    }), [checklistValues, propertyForm?.price]);

  const checklistExportOptions = useMemo(() => {
    const options = [{
      id: 'checklist',
      label: 'Splošne informacije',
      type: 'checklist',
      enabled: filledChecklistItems.length > 0,
      pdfDocuments: [],
      totalDocuments: 0
    }];

    MASTER_PARCEL_TEMPLATE_PRESET.items
      .filter((item) => isChecklistDocumentCapable(item))
      .forEach((item) => {
        const documentValues = getChecklistItemDocumentValues(item, checklistValues[item.id], propertyForm);
        const pdfDocuments = documentValues.filter((documentValue) => isPdfDocumentValue(documentValue));

        options.push({
          id: item.id,
          label: item.label,
          type: 'documents',
          enabled: pdfDocuments.length > 0,
          pdfDocuments,
          totalDocuments: documentValues.length
        });
      });

    return options;
  }, [checklistValues, filledChecklistItems.length, propertyForm]);

  const renderChecklistCustomField = useMemo(() => createMasterParcelCustomFieldRenderer({
    propertyForm,
    fetchZkParcelData,
    downloadZkExtract: (zkExtractId, ko, idParceleStavbe) => handleDownloadZkFile({ zkExtractId, ko, idParceleStavbe, history: false }),
    downloadZkHistory: (zkExtractId, ko, idParceleStavbe) => handleDownloadZkFile({ zkExtractId, ko, idParceleStavbe, history: true }),
    formatEuro,
    computePricePerSquareMeter,
    uploadChecklistDocument: (file) => handleChecklistDocumentUpload(null, file),
    downloadChecklistDocument: (value) => handleDocumentDownload(value?.documentPath, value?.documentName),
    documentAccept: DOCUMENT_ACCEPT
  }), [fetchZkParcelData, propertyForm]);

  const renderChecklistItemContent = useCallback((item, value, options = {}) => {
    const exportMode = options.exportMode === true;

    if (String(item.type || '').startsWith('mpt_')) {
      return renderMasterParcelSummary({
        item,
        value,
        propertyForm,
        price: propertyForm?.price,
        formatEuro,
        downloadZkExtract: (zkExtractId, ko, idParceleStavbe) => handleDownloadZkFile({ zkExtractId, ko, idParceleStavbe, history: false }),
        downloadZkHistory: (zkExtractId, ko, idParceleStavbe) => handleDownloadZkFile({ zkExtractId, ko, idParceleStavbe, history: true }),
        downloadChecklistDocument: (documentValue) => handleDocumentDownload(documentValue?.documentPath, documentValue?.documentName),
        propertyContacts,
        onOpenPropertyContact,
        exportMode
      });
    }

    if (item.type === 'document') {
      const documentValues = getChecklistDocumentValues(item, value);

      if (exportMode) {
        return (
          <div style={{ display: 'grid', gap: '6px', color: '#475569' }}>
            {documentValues.map((documentValue, index) => (
              <div key={`${item.id}-export-${documentValue.documentPath}-${index}`}>{documentValue.documentName}</div>
            ))}
          </div>
        );
      }

      if (documentValues.length === 0) {
        return <div style={{ color: '#94a3b8', fontSize: '13px' }}>No document uploaded.</div>;
      }

      return (
        <div style={{ display: 'grid', gap: '8px' }}>
          {documentValues.map((documentValue, index) => (
            <button
              key={`${item.id}-${documentValue.documentPath}-${index}`}
              type="button"
              onClick={() => handleDocumentDownload(documentValue.documentPath, documentValue.documentName)}
              style={{ padding: '7px 10px', backgroundColor: '#8b5cf6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '6px', width: 'fit-content' }}
            >
              {documentValue.documentName}
            </button>
          ))}
        </div>
      );
    }

    return <div style={{ color: '#1e293b', fontWeight: '500', whiteSpace: 'pre-wrap' }}>{formatChecklistValue(item, value)}</div>;
  }, [handleDocumentDownload, onOpenPropertyContact, propertyContacts, propertyForm]);

  const handleSaveChecklistValues = async (nextValues) => {
    if (!entityId || !entityType) {
      return;
    }

    setChecklistSaving(true);
    try {
      await ensureTemplate();
      const response = await authFetch(`${API_BASE}/api/checklists/entry`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scope,
          typeKey: MPT_TYPE_KEY,
          entityType,
          entityId,
          values: nextValues,
          userName: localStorage.getItem('leadGenUser') || 'User'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save checklist');
      }

      const data = await response.json();
      setChecklistValues(data.values && typeof data.values === 'object' ? data.values : {});
      setShowChecklistModal(false);
      setExpanded(true);
    } catch (error) {
      console.error(error);
      window.alert(`Could not save checklist: ${error.message}`);
    } finally {
      setChecklistSaving(false);
    }
  };

  const handleOpenChecklistExportModal = useCallback(() => {
    const defaultSelection = checklistExportOptions.filter((option) => option.enabled).map((option) => option.id);

    if (defaultSelection.length === 0) {
      window.alert('Nothing is available for PDF export yet.');
      return;
    }

    setChecklistExportSelection(defaultSelection);
    setChecklistExportIncludeCoverPage(true);
    setChecklistExportIncludeItemName(false);
    setShowChecklistExportModal(true);
  }, [checklistExportOptions]);

  const handleToggleChecklistExportOption = useCallback((optionId) => {
    setChecklistExportSelection((current) => current.includes(optionId)
      ? current.filter((value) => value !== optionId)
      : [...current, optionId]);
  }, []);

  const handleExportChecklistPdf = async () => {
    const selectedOptions = checklistExportOptions.filter((option) => checklistExportSelection.includes(option.id));
    if (selectedOptions.length === 0) {
      window.alert('Select at least one export option.');
      return;
    }

    setChecklistExporting(true);

    try {
      const mergedPdf = await PDFDocument.create();
      const { regularFont, boldFont } = await loadPdfFonts(mergedPdf);
      const zkValue = checklistValues[MPT_ZK_ITEM_ID];
      const extraParcels = Array.isArray(zkValue?.extraParcels) ? zkValue.extraParcels : [];
      const parcelNumbers = [String(propertyForm?.idParceleStavbe || '').trim(), ...extraParcels.map((parcel) => String(parcel?.parcela || '').trim())].filter(Boolean);
      const parcelArea = String(checklistValues[MPT_AREA_SECTION_ITEM_ID]?.area || '').trim();
      const pricePerSquareMeter = computePricePerSquareMeter({ area: parcelArea, price: propertyForm?.price });

      if (checklistExportIncludeCoverPage) {
        await addPdfCoverPage(mergedPdf, {
          propertyForm,
          parcelNumbers,
          parcelArea,
          pricePerSquareMeter,
          regularFont,
          boldFont
        });
      }

      const overlayFont = checklistExportIncludeItemName ? boldFont : null;
      const exportWarnings = [];
      const includesChecklistSummary = selectedOptions.some((option) => option.id === 'checklist');

      if (includesChecklistSummary) {
        flushSync(() => {
          setChecklistExportSurfaceVisible(true);
        });
        await waitForNextPaint();
      }

      for (const option of selectedOptions) {
        if (option.id === 'checklist') {
          await addChecklistSummaryFromDomToPdf(mergedPdf, checklistExportRef.current, {
            overlayEnabled: checklistExportIncludeItemName,
            overlayLabel: option.label,
            overlayFont
          });
          continue;
        }

        for (const documentValue of option.pdfDocuments) {
          try {
            if (mergedPdf.getPageCount() % 2 === 1) {
              mergedPdf.addPage(PageSizes.A4);
            }

            const pdfBytes = await fetchChecklistDocumentArrayBuffer(documentValue);
            await appendPdfToDocument(mergedPdf, pdfBytes, {
              overlayEnabled: checklistExportIncludeItemName,
              overlayLabel: option.label,
              overlayFont
            });
          } catch (error) {
            console.error('Could not include checklist export document:', error);
            exportWarnings.push(`${option.label}: ${documentValue?.documentName || 'document'}`);
          }
        }
      }

      if (mergedPdf.getPageCount() === 0) {
        throw new Error('No PDF content was available for the selected export items.');
      }

      const mergedPdfBytes = await mergedPdf.save();
      downloadArrayBufferAsFile(
        mergedPdfBytes,
        `mpt_checklist_${propertyForm?.katastrskaObcina || 'property'}_${propertyForm?.idParceleStavbe || 'export'}_${new Date().toISOString().split('T')[0]}.pdf`
      );

      setShowChecklistExportModal(false);

      if (exportWarnings.length > 0) {
        window.alert(`Export completed, but some PDFs could not be included:\n${exportWarnings.join('\n')}`);
      }
    } catch (error) {
      console.error(error);
      window.alert(`Could not export checklist PDF: ${error.message}`);
    } finally {
      flushSync(() => {
        setChecklistExportSurfaceVisible(false);
      });
      setChecklistExporting(false);
    }
  };

  if (!entityId || !entityType) {
    return null;
  }

  return (
    <>
      <div style={{ borderRadius: '16px', padding: '18px', border: '1px solid #e2e8f0', backgroundColor: 'white', boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginBottom: expanded ? '16px' : 0, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => setExpanded((current) => !current)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', padding: 0, border: 'none', background: 'transparent', color: '#0f172a', cursor: 'pointer' }}
          >
            <CheckSquare size={20} color="#d97706" />
            <span style={{ fontSize: '20px', fontWeight: '700' }}>{title}</span>
            {expanded ? <ChevronUp size={18} color="#64748b" /> : <ChevronDown size={18} color="#64748b" />}
          </button>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={handleOpenChecklistExportModal}
              disabled={checklistLoading}
              style={{ padding: '8px 12px', borderRadius: '999px', border: '1px solid #bfdbfe', backgroundColor: '#eff6ff', color: '#1d4ed8', cursor: checklistLoading ? 'not-allowed' : 'pointer', fontWeight: '600' }}
            >
              Export to PDF
            </button>
            <button
              type="button"
              onClick={() => setShowChecklistModal(true)}
              style={{ padding: '8px 12px', borderRadius: '999px', border: '1px solid #fdba74', backgroundColor: '#fff7ed', color: '#9a3412', cursor: 'pointer', fontWeight: '600' }}
            >
              Edit
            </button>
          </div>
        </div>

        {expanded ? (
          <div style={{ display: 'grid', gap: '18px' }}>
            {checklistLoading ? (
              <div style={{ color: '#94a3b8', fontSize: '14px' }}>Loading checklist...</div>
            ) : filledChecklistItems.length === 0 ? (
              <div style={{ color: '#94a3b8', fontSize: '14px' }}>No checklist items filled yet.</div>
            ) : (
              <div style={{ display: 'grid', gap: '10px' }}>
                {filledChecklistItems.map(({ item, value }) => (
                  <div key={item.id} style={{ padding: '12px 14px', backgroundColor: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                    <div style={{ color: '#64748b', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>{item.label}</div>
                    {renderChecklistItemContent(item, value)}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : null}
      </div>

      <UniversalChecklistModal
        isOpen={showChecklistModal}
        title={title}
        typeLabel={propertyForm?.type || ''}
        items={MASTER_PARCEL_TEMPLATE_PRESET.items}
        values={checklistValues}
        loading={checklistLoading}
        saving={checklistSaving}
        onClose={() => setShowChecklistModal(false)}
        onSaveValues={handleSaveChecklistValues}
        onUploadDocument={handleChecklistDocumentUpload}
        onDownloadDocument={(value) => handleDocumentDownload(value?.documentPath, value?.documentName)}
        documentAccept={DOCUMENT_ACCEPT}
        renderCustomField={renderChecklistCustomField}
        allowMultipleDocuments={isMultiDocumentChecklistItem}
        templateEditingEnabled={false}
      />

      {showChecklistExportModal ? (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.48)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '20px' }}>
          <div style={{ width: '100%', maxWidth: '640px', backgroundColor: 'white', borderRadius: '16px', padding: '22px', boxShadow: '0 20px 25px -5px rgba(15, 23, 42, 0.25)' }}>
            <h3 style={{ marginTop: 0, color: '#0f172a' }}>Export checklist to PDF</h3>
            <div style={{ color: '#475569', fontSize: '14px', marginBottom: '14px' }}>
              Select the checklist summary and any checklist items with PDF documents you want to merge into one file.
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px', color: '#0f172a', fontWeight: '500' }}>
              <input
                type="checkbox"
                checked={checklistExportIncludeCoverPage}
                disabled={checklistExporting}
                onChange={(event) => setChecklistExportIncludeCoverPage(event.target.checked)}
              />
              Include first page
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px', color: '#0f172a', fontWeight: '500' }}>
              <input
                type="checkbox"
                checked={checklistExportIncludeItemName}
                disabled={checklistExporting}
                onChange={(event) => setChecklistExportIncludeItemName(event.target.checked)}
              />
              Overlay item name on each exported page
            </label>

            <div style={{ display: 'grid', gap: '10px', maxHeight: '360px', overflowY: 'auto' }}>
              {checklistExportOptions.map((option) => {
                const countLabel = option.id === 'checklist'
                  ? 'Rendered from the checklist view'
                  : option.enabled
                    ? `${option.pdfDocuments.length} PDF${option.pdfDocuments.length === 1 ? '' : 's'} available`
                    : option.totalDocuments > 0
                      ? 'No PDF documents available'
                      : 'No documents uploaded';

                return (
                  <label
                    key={`export-option-${option.id}`}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '10px',
                      padding: '12px 14px',
                      borderRadius: '12px',
                      border: '1px solid #e2e8f0',
                      backgroundColor: option.enabled ? '#f8fafc' : '#f1f5f9',
                      opacity: option.enabled ? 1 : 0.65,
                      cursor: option.enabled ? 'pointer' : 'not-allowed'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checklistExportSelection.includes(option.id)}
                      disabled={!option.enabled || checklistExporting}
                      onChange={() => handleToggleChecklistExportOption(option.id)}
                      style={{ marginTop: '3px' }}
                    />
                    <div style={{ display: 'grid', gap: '4px' }}>
                      <div style={{ color: '#0f172a', fontWeight: '600' }}>{option.label}</div>
                      <div style={{ color: '#64748b', fontSize: '13px' }}>{countLabel}</div>
                    </div>
                  </label>
                );
              })}
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '18px', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={handleExportChecklistPdf}
                disabled={checklistExporting}
                style={{ flex: 1, minWidth: '180px', padding: '10px 14px', borderRadius: '10px', border: 'none', backgroundColor: '#2563eb', color: 'white', cursor: checklistExporting ? 'progress' : 'pointer', fontWeight: '600' }}
              >
                {checklistExporting ? 'Exporting...' : 'Create PDF'}
              </button>
              <button
                type="button"
                onClick={() => setShowChecklistExportModal(false)}
                disabled={checklistExporting}
                style={{ flex: 1, minWidth: '180px', padding: '10px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', backgroundColor: '#f8fafc', color: '#0f172a', cursor: checklistExporting ? 'not-allowed' : 'pointer', fontWeight: '600' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {checklistExportSurfaceVisible ? (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(255,255,255,0.96)', zIndex: 2147483647, overflow: 'auto', padding: '24px' }} aria-hidden="true">
          <div style={{ width: '210mm', margin: '0 auto', padding: 0, backgroundColor: '#ffffff' }}>
            <div ref={checklistExportRef}>
              <div style={{ width: '210mm', boxSizing: 'border-box', padding: '10mm', backgroundColor: '#ffffff', display: 'inline-block' }}>
                <div style={{ marginBottom: '18px' }}>
                  <div style={{ color: '#0f172a', fontSize: '22px', fontWeight: '700', marginBottom: '6px' }}>Splošne informacije</div>
                  <div style={{ color: '#64748b', fontSize: '13px' }}>{propertyForm?.katastrskaObcina} {propertyForm?.idParceleStavbe}</div>
                </div>
                {filledChecklistItems.length === 0 ? (
                  <div style={{ color: '#94a3b8', fontSize: '14px' }}>Splošne informacije še nimajo izpolnjenih postavk.</div>
                ) : (
                  <div style={{ display: 'grid', gap: '10px' }}>
                    {filledChecklistItems.map(({ item, value }) => (
                      <div className="checklist-export-item" key={`export-${item.id}`} style={{ padding: '12px 14px', backgroundColor: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0', breakInside: 'avoid', pageBreakInside: 'avoid' }}>
                        <div style={{ color: '#64748b', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>{item.label}</div>
                        {renderChecklistItemContent(item, value, { exportMode: true })}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default MasterParcelChecklistSection;